"use client"

import { useState, useEffect, useRef } from "react"
import { X, User, Send, Loader2 } from "lucide-react"
import { useAuth } from "../../context/AuthContext"
import { dictionary } from "../../utils/dictionary"
import { useCancelChat } from "../../hooks/useCancelChat"
import { CancelChatMessage } from "../../services/membership/cancelChatService"

interface CancelChatModalProps {
  isOpen: boolean
  onClose: () => void
  onCanCancel?: (lastUserMessage: string | null) => void
}

export function CancelChatModal({ isOpen, onClose, onCanCancel }: CancelChatModalProps) {
  const { language } = useAuth()
  const [message, setMessage] = useState("")
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)

  const {
    messages,
    canCancel,
    isLoading,
    sendMessage,
    initializeChat,
    getLastUserMessage,
    clearChat,
  } = useCancelChat(language)

  useEffect(() => {
    if (isOpen) {
      initializeChat()
    }
  }, [isOpen, initializeChat])

  useEffect(() => {
    if (canCancel && onCanCancel) {
      const lastUserMessage = getLastUserMessage()
      clearChat();
      onCanCancel(lastUserMessage)
    }
  }, [canCancel, onCanCancel, getLastUserMessage])

  useEffect(() => {
    scrollToBottom()
  }, [messages, isLoading])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }

  const handleSend = async () => {
    if (message.trim() && !isLoading) {
      const messageToSend = message.trim()
      setMessage("")
      await sendMessage(messageToSend)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && message.trim() && !isLoading) {
      handleSend()
    }
  }

  const initialMessage =
    dictionary["tell-me-what-problems-are-you-finding-with-fluxi"]?.[language] ||
    "Cuéntame, ¿qué problemas estás encontrando con Fluxi que no te permiten aprovechar tu membresía completamente?"

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative bg-white rounded-3xl shadow-lg max-w-2xl w-full mx-4 overflow-hidden h-[600px] flex flex-col">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header con gradiente */}
        <div
          className="text-white p-6"
          style={{
            background: "linear-gradient(to right, rgb(80, 80, 246), rgb(133, 162, 255))",
          }}
        >
          <div className="flex items-center space-x-3">
            <div>
              <h3 className="text-lg font-semibold">
                {dictionary["fluxi-support"]?.[language] || "Soporte de Fluxi"}
              </h3>
              <p className="text-blue-100 text-sm">
                {dictionary["we-are-here-to-help-you"]?.[language] || "Estamos aquí para ayudarte"}
              </p>
            </div>
          </div>
        </div>

        {/* Área de mensajes */}
        <div
          ref={messagesContainerRef}
          className="flex-1 overflow-y-auto p-6 space-y-4"
        >
          {/* Mensaje inicial si no hay mensajes */}
          {messages.length === 0 && (
            <div className="flex justify-start">
              <div className="flex items-start space-x-2 max-w-[80%]">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-gray-200">
                  <User className="w-4 h-4 text-gray-600" />
                </div>
                <div className="p-3 rounded-2xl bg-gray-100 text-gray-900">
                  <p className="text-sm leading-relaxed">{initialMessage}</p>
                </div>
              </div>
            </div>
          )}

          {/* Mensajes del chat */}
          {messages.map((msg: CancelChatMessage) => (
            <div
              key={msg.id}
              className={`flex ${msg.sender === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`flex items-start space-x-2 max-w-[80%] ${
                  msg.sender === "user" ? "flex-row-reverse space-x-reverse" : ""
                }`}
              >
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-gray-200">
                  <User className="w-4 h-4 text-gray-600" />
                </div>
                <div
                  className={`p-3 rounded-2xl text-sm leading-relaxed ${
                    msg.sender === "user"
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 text-gray-900"
                  }`}
                >
                  <p>{msg.text}</p>
                </div>
              </div>
            </div>
          ))}

          {/* Indicador de carga */}
          {isLoading && (
            <div className="flex justify-start">
              <div className="flex items-start space-x-2 max-w-[80%]">
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-gray-200">
                  <User className="w-4 h-4 text-gray-600" />
                </div>
                <div className="p-3 rounded-2xl bg-gray-100 text-gray-900">
                  <Loader2 className="w-4 h-4 animate-spin text-gray-600" />
                </div>
              </div>
            </div>
          )}

          {/* Ref para auto-scroll */}
          <div ref={messagesEndRef} />
        </div>

        {/* Input y botón de enviar */}
        <div className="border-t border-gray-200 p-4">
          <div className="flex space-x-2">
            <input
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isLoading}
              placeholder={
                dictionary["write-your-message"]?.[language] || "Escribe tu mensaje..."
              }
              className="flex-1 p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed"
            />
            <button
              onClick={handleSend}
              disabled={!message.trim() || isLoading}
              className={`px-4 py-3 rounded-xl transition-colors ${
                message.trim() && !isLoading
                  ? "bg-blue-600 hover:bg-blue-700 text-white cursor-pointer"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed"
              }`}
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
